import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useSocketStore } from './socket';
import { apiFetch } from '../services/api';

/** A player's own metadata bag — the integration's own rows. */
export interface MediaPlayerMetadata {
  [key: string]: unknown;
}

export interface MediaPlayer {
  id: string;
  deviceId: string;
  integrationId: string;
  playerName: string;
  playerType: 'dlna_server' | 'dlna_renderer' | 'chromecast' | 'spotify' | 'plex' | 'local';
  hostname?: string;
  port?: number;
  username?: string;
  uuid?: string;
  manufacturer?: string;
  modelName?: string;
  iconUrl?: string;
  connected: boolean;
  available: boolean;
  metadata?: MediaPlayerMetadata;
  createdAt: string;
  updatedAt: string;
}

export interface MediaLibraryItem {
  id: string;
  title: string;
  artist?: string;
  album?: string;
  genre?: string;
  year?: number;
  trackNumber?: number;
  duration?: number;
  filePath: string;
  mimeType: string;
  fileSize?: number | string;
  thumbnailPath?: string;
  mediaType: 'audio' | 'video';
  createdAt: string;
  updatedAt?: string;
  /**
   * External (non-library) source support: when present the browser player
   * streams this absolute URL directly instead of deriving the stream from the
   * local media-library endpoint. Used by Plex playback.
   */
  url?: string;
  /** Absolute thumbnail URL for external sources (Plex). */
  thumb?: string;
}

export interface MediaLibraryItemUpdateInput {
  title?: string;
  artist?: string | null;
  album?: string | null;
  genre?: string | null;
  year?: number | null;
  trackNumber?: number | null;
}

export interface MediaLibraryUploadResult {
  imported: number;
  failed: number;
  errors: string[];
}

export interface PlaybackState {
  isPlaying: boolean;
  volume: number;
  muted: boolean;
  playState?: 'playing' | 'paused' | 'stopped' | 'idle' | 'buffering';
  title?: string;
  artist?: string;
  album?: string;
  thumbnailUrl?: string;
  duration?: number;
  position?: number;
  shuffle?: boolean;
  repeat?: 'off' | 'track' | 'context';
  source?: string;
}

/**
 * The backend /state endpoint and some socket payloads deliver a "raw" shape
 * (playState + nested track.{title,artist,...}) while the UI consumes a flat
 * shape (isPlaying + top-level title/artist/...). Normalize any input so the
 * rest of the app can rely on one consistent shape.
 */
/** A playback push's nested track row (the hub sends either shape). */
export interface RawPlaybackTrack {
  title?: string;
  artist?: string;
  album?: string;
  thumbnailUrl?: string;
  durationMs?: number;
  positionMs?: number;
}

/**
 * A playback push in either hub shape: the flat fields and/or a nested `track`.
 * Every field is read defensively below.
 */
export interface RawPlaybackState {
  isPlaying?: boolean;
  playState?: PlaybackState['playState'];
  track?: RawPlaybackTrack;
  volume?: number;
  muted?: boolean;
  title?: string;
  artist?: string;
  album?: string;
  thumbnailUrl?: string;
  durationMs?: number;
  positionMs?: number;
  duration?: number;
  position?: number;
  shuffle?: boolean;
  repeat?: PlaybackState['repeat'];
  source?: string;
}

export function normalizePlaybackState(raw: RawPlaybackState): PlaybackState | null {
  if (!raw || typeof raw !== 'object') return null;
  const track: RawPlaybackTrack = raw.track ?? {};
  const playState = raw.playState;
  const isPlaying =
    raw.isPlaying !== undefined ? !!raw.isPlaying : playState === 'playing' || playState === 'buffering';
  const durationMs = raw.durationMs ?? track.durationMs;
  const positionMs = raw.positionMs ?? track.positionMs;
  return {
    isPlaying,
    volume: raw.volume ?? 50,
    muted: raw.muted ?? false,
    playState: playState || (isPlaying ? 'playing' : 'idle'),
    title: raw.title ?? track.title,
    artist: raw.artist ?? track.artist,
    album: raw.album ?? track.album,
    thumbnailUrl: raw.thumbnailUrl ?? track.thumbnailUrl,
    duration: raw.duration ?? (durationMs != null ? Math.round(durationMs / 1000) : undefined),
    position: raw.position ?? (positionMs != null ? Math.round(positionMs / 1000) : undefined),
    shuffle: raw.shuffle,
    repeat: raw.repeat,
    source: raw.source,
  };
}

export type PlaybackCommandType =
  | 'play'
  | 'pause'
  | 'stop'
  | 'next'
  | 'previous'
  | 'seek'
  | 'set_volume'
  | 'mute'
  | 'unmute'
  | 'toggle_shuffle'
  | 'set_repeat'
  | 'cast'
  | 'transfer';

export interface PlaybackCommand {
  command: PlaybackCommandType;
  value?: unknown;
  uri?: string;
  contentType?: string;
  title?: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  deviceId?: string;
}

export const useMediaPlayersStore = defineStore('mediaPlayers', () => {
  const socketStore = useSocketStore();

  // State
  const players = ref<MediaPlayer[]>([]);
  const libraryItems = ref<MediaLibraryItem[]>([]);
  const playbackStates = ref<Map<string, PlaybackState>>(new Map());
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref(0);

  // Active player for the Now Playing bar
  const activePlayerId = ref<string | null>(null);

  // Computed
  const playersByIntegration = computed(() => {
    const map = new Map<string, MediaPlayer[]>();
    players.value.forEach((p) => {
      if (!map.has(p.integrationId)) map.set(p.integrationId, []);
      map.get(p.integrationId)!.push(p);
    });
    return map;
  });

  const activePlayer = computed(() =>
    activePlayerId.value ? players.value.find((p) => p.id === activePlayerId.value) : null,
  );

  const activePlaybackState = computed(() =>
    activePlayerId.value ? playbackStates.value.get(activePlayerId.value) || null : null,
  );

  const connectedPlayers = computed(() => players.value.filter((p) => p.connected));

  const audioItems = computed(() => libraryItems.value.filter((i) => i.mediaType === 'audio'));
  const videoItems = computed(() => libraryItems.value.filter((i) => i.mediaType === 'video'));

  function getPlayer(id: string) {
    return players.value.find((p) => p.id === id);
  }
  function getPlayersByIntegration(integrationId: string) {
    return players.value.filter((p) => p.integrationId === integrationId);
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  async function initialize() {
    if (loaded.value) return;
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/media-players');
      players.value = data;
      loaded.value = true;
      lastUpdated.value = Date.now();
      setupSocketListeners();
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to load media players';
      console.error('[MediaPlayersStore] Failed to initialize:', err);
    } finally {
      loading.value = false;
    }
  }

  async function refresh() {
    loading.value = true;
    try {
      const data = await apiFetch('/media-players');
      players.value = data;
      lastUpdated.value = Date.now();
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to refresh media players';
    } finally {
      loading.value = false;
    }
  }

  async function loadLibrary(params?: { mediaType?: string; search?: string; offset?: number; limit?: number }) {
    try {
      const qs = new URLSearchParams();
      if (params?.mediaType) qs.set('mediaType', params.mediaType);
      if (params?.search) qs.set('search', params.search);
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      const data = await apiFetch(`/media-library${query ? '?' + query : ''}`);
      libraryItems.value = data.items || data;
      return data;
    } catch (err) {
      console.error('[MediaPlayersStore] Failed to load library:', err);
      throw err;
    }
  }

  async function uploadLibraryFiles(files: File[] | FileList): Promise<MediaLibraryUploadResult> {
    const selectedFiles = Array.from(files);
    if (selectedFiles.length === 0) {
      return { imported: 0, failed: 0, errors: [] };
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));

    loading.value = true;
    error.value = null;
    try {
      const result = await apiFetch<MediaLibraryUploadResult>('/media-library/upload', {
        method: 'POST',
        body: formData,
      });
      await loadLibrary();
      return {
        imported: result.imported ?? 0,
        failed: result.failed ?? 0,
        errors: result.errors ?? [],
      };
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err?.message || 'Failed to upload media files';
      console.error('[MediaPlayersStore] Failed to upload library files:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteLibraryItem(id: string): Promise<void> {
    await apiFetch(`/media-library/${id}`, { method: 'DELETE' });
    const i = libraryItems.value.findIndex((item) => item.id === id);
    if (i !== -1) libraryItems.value.splice(i, 1);
  }

  async function updateLibraryItem(id: string, input: MediaLibraryItemUpdateInput): Promise<MediaLibraryItem> {
    const updated = await apiFetch<MediaLibraryItem>(`/media-library/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    patchLibraryItem(updated);
    return updated;
  }

  async function fetchPlaybackState(playerId: string) {
    try {
      const state = await apiFetch(`/media-players/${playerId}/state`);
      const normalized = normalizePlaybackState(state);
      if (normalized) {
        playbackStates.value.set(playerId, normalized);
      }
      return playbackStates.value.get(playerId) || null;
    } catch {
      return null;
    }
  }

  async function sendCommand(playerId: string, command: PlaybackCommand) {
    await apiFetch(`/media-players/${playerId}/command`, {
      method: 'POST',
      body: JSON.stringify(command),
    });
    // Optimistically set active player
    if (command.command === 'play' || command.command === 'cast') {
      activePlayerId.value = playerId;
    } else if (command.command === 'stop') {
      if (activePlayerId.value === playerId) activePlayerId.value = null;
    }
  }

  async function deletePlayer(id: string) {
    await apiFetch(`/media-players/${id}`, { method: 'DELETE' });
    removePlayer(id);
  }

  async function discoverDevices(integrationId: string) {
    return apiFetch(`/integrations/${integrationId}/media-players/discover`, { method: 'POST' });
  }

  async function getSpotifyAuthUrl(integrationId: string) {
    const data = await apiFetch(`/integrations/${integrationId}/spotify/auth-url`);
    return data.url as string;
  }

  async function completeSpotifyAuth(integrationId: string, code: string) {
    return apiFetch(`/integrations/${integrationId}/spotify/callback`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async function completeSonosAuth(integrationId: string, code: string) {
    return apiFetch(`/integrations/${integrationId}/sonos/callback`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // ── Optimistic helpers ────────────────────────────────────────────────────

  function addPlayer(player: MediaPlayer) {
    if (!players.value.some((p) => p.id === player.id)) players.value.push(player);
  }

  function updatePlayer(id: string, updates: Partial<MediaPlayer>) {
    const i = players.value.findIndex((p) => p.id === id);
    if (i !== -1) players.value[i] = { ...players.value[i], ...updates };
  }

  function removePlayer(id: string) {
    const i = players.value.findIndex((p) => p.id === id);
    if (i !== -1) players.value.splice(i, 1);
  }

  function patchLibraryItem(item: MediaLibraryItem) {
    const i = libraryItems.value.findIndex((it) => it.id === item.id);
    if (i !== -1) libraryItems.value[i] = { ...libraryItems.value[i], ...item };
    else libraryItems.value.push(item);
  }

  function setActivePlayer(playerId: string | null) {
    activePlayerId.value = playerId;
  }

  // ── Socket listeners ──────────────────────────────────────────────────────

  function setupSocketListeners() {
    const socket = socketStore.socket;
    if (!socket) return;

    socket.on('media_player:created', (data: MediaPlayer) => {
      addPlayer(data);
      lastUpdated.value = Date.now();
    });

    socket.on('media_player:updated', (data: MediaPlayer) => {
      updatePlayer(data.id, data);
      lastUpdated.value = Date.now();
    });

    socket.on('media_player:deleted', (data: { id: string }) => {
      removePlayer(data.id);
      if (activePlayerId.value === data.id) activePlayerId.value = null;
      lastUpdated.value = Date.now();
    });

    socket.on('playback_state_changed', (data: { integrationId: string; playerId: string; state: PlaybackState }) => {
      const normalized = normalizePlaybackState(data.state);
      if (normalized) {
        playbackStates.value.set(data.playerId, normalized);
        // Auto-set active player when something starts playing
        if (normalized.isPlaying && !activePlayerId.value) {
          activePlayerId.value = data.playerId;
        }
      }
    });

    socket.on('media_library:items_added', () => loadLibrary());
    socket.on('media_library:item_deleted', ({ id }: { id: string }) => {
      const i = libraryItems.value.findIndex((it) => it.id === id);
      if (i !== -1) libraryItems.value.splice(i, 1);
    });
    socket.on('media_library:item_updated', (item: MediaLibraryItem | { id: string }) => {
      if ('title' in item) patchLibraryItem(item as MediaLibraryItem);
      else loadLibrary();
    });
  }

  function isStale() {
    return Date.now() - lastUpdated.value > 30000;
  }

  return {
    // State
    players,
    libraryItems,
    playbackStates,
    loading,
    loaded,
    error,
    lastUpdated,
    activePlayerId,
    // Computed
    playersByIntegration,
    activePlayer,
    activePlaybackState,
    connectedPlayers,
    audioItems,
    videoItems,
    // Getters
    getPlayer,
    getPlayersByIntegration,
    // Actions
    initialize,
    refresh,
    loadLibrary,
    uploadLibraryFiles,
    deleteLibraryItem,
    updateLibraryItem,
    fetchPlaybackState,
    sendCommand,
    deletePlayer,
    discoverDevices,
    getSpotifyAuthUrl,
    completeSpotifyAuth,
    completeSonosAuth,
    // Optimistic
    addPlayer,
    updatePlayer,
    removePlayer,
    patchLibraryItem,
    setActivePlayer,
    isStale,
  };
});
